import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { useWideLayout } from "../../layout/shell-layout";
import { AreaLinks } from "../../operations/components/parts";
import { operationsKeys, useOperationsJobs, useOperationsSummary } from "../../operations/queries";
import { useOperationsRuntime } from "../../operations/runtime";
import { usePaymentsRuntime } from "../../payments/runtime";
import { AsyncState } from "../../ui/async-state";
import { PageHeader } from "../../ui/page-header";
import { StatusMessage } from "../../ui/status-message";
import { useDiagnosticsCapabilities } from "../access";
import type { Dialog } from "../components/dialog-state";
import {
  NotificationFailuresSection,
  PaymentProblemsSection,
} from "../components/ExceptionSections";
import { FailedJobsSection, TaskFamiliesSection } from "../components/JobSections";
import { ProviderHealthSection } from "../components/ProviderHealthSection";
import {
  ControlDialog,
  ReconcileDialog,
  RequeueDialog,
  ResolveExceptionDialog,
} from "../components/RecoveryDialogs";
import { paymentEvidenceLabel } from "../labels";
import {
  diagnosticsKeys,
  useNotificationFailures,
  usePaymentExceptions,
  useProviderHealth,
} from "../queries";
import { useDiagnosticsRuntime } from "../runtime";

// The Diagnostics page (CHT-772; docs/engineering/admin-ia.md section 13). One screen to find what is wrong and to do the SUPPORTED thing about it:
//   1. provider health (the typed read model of CHT-770): each failure links to the record it was about, where the staff member may open it;
//   2. the failed jobs and the task queues (the operator read model of CHT-761): re-run one job, pause or resume a task family;
//   3. the payment problems: the evidence label, the open exceptions, reconcile one payment, resolve one exception;
//   4. the notification failures (the delivery records of CHT-764).
// Every section shows a link to its runbook. Every action opens a confirmation dialog with ONE idempotency key. There is no "retry everything"
// and no control that edits a provider or a database row. A failed action stays visible: this page never marks an incident resolved, the
// record does. Health is a pointer, never proof (docs/engineering/observability.md section 1).
// The page owns the queries and the open dialog; each section lives in `../components` (provider health, the jobs and queues, the payment and
// notification problems) and asks for a dialog with `onOpen`.
//
// The tables are the kit `DataTable` (a row becomes a card under 720 px of table width). Their column lists are module constants and the
// rows carry what a cell needs: `DataTable` draws a cell with `flexRender`, which treats a cell function as a component, so a column list
// made again on every render would throw away every button of the table on every render. Every action button has its own name: its visible
// word plus a hidden tail that says which row (the first eight characters of the id, the task, the state: never free text), so no two controls share a name.

type Notice = Readonly<{ text: string; family: string }>;

export function DiagnosticsPage() {
  useWideLayout();
  const { capabilities, isLoading: rolesLoading } = useDiagnosticsCapabilities();
  const { api } = useDiagnosticsRuntime();
  const operations = useOperationsRuntime();
  const { payments } = usePaymentsRuntime();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const health = useProviderHealth(capabilities.read);
  const summary = useOperationsSummary(capabilities.read);
  const failedJobs = useOperationsJobs({ state: "FAILED", task: null }, capabilities.read);
  const notifications = useNotificationFailures(capabilities.read);
  const exceptions = usePaymentExceptions(capabilities.read && capabilities.resolveException);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include the diagnostics."
      />
    );
  }

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: diagnosticsKeys.all });
    void queryClient.invalidateQueries({ queryKey: operationsKeys.all });
  };
  const finish = (family: string) => (text: string) => {
    setDialog(null);
    setNotice({ text, family });
    reload();
  };
  const razorpay = health.data?.providers.find((entry) => entry.provider === "razorpay");
  const evidence = paymentEvidenceLabel(razorpay?.mode ?? "off");

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Lafayette operations"
        title="Diagnostics"
        titleId="page-title"
        description="Find what is wrong, open the record it is about, and do the supported thing. Health is a pointer, not proof: the record it links to is the truth."
        actions={
          <AreaLinks
            links={[
              { to: "/operations", label: "Jobs and queues" },
              ...(capabilities.audit ? [{ to: "/operations/audit", label: "Audit trail" }] : []),
              { to: "/operations/runbooks/index", label: "All runbooks" },
            ]}
          />
        }
      />

      {notice === null ? null : (
        <StatusMessage
          message={{
            tone: "success",
            text: (
              <>
                {notice.text} It is recorded in the audit trail.{" "}
                {capabilities.audit ? (
                  <Link
                    className="underline underline-offset-4"
                    to={`/operations/audit?action=${encodeURIComponent(notice.family)}`}
                  >
                    See the audit trail
                  </Link>
                ) : null}
              </>
            ),
          }}
        />
      )}

      <ProviderHealthSection health={health} capabilities={capabilities} onOpen={setDialog} />
      <FailedJobsSection failedJobs={failedJobs} capabilities={capabilities} onOpen={setDialog} />
      <TaskFamiliesSection summary={summary} capabilities={capabilities} onOpen={setDialog} />
      <PaymentProblemsSection
        exceptions={exceptions}
        evidence={evidence}
        capabilities={capabilities}
        onOpen={setDialog}
      />
      <NotificationFailuresSection notifications={notifications} />

      {dialog?.kind === "requeue" ? (
        <RequeueDialog
          api={operations.api}
          jobId={dialog.jobId}
          taskLabel={dialog.taskLabel}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={finish("background_job")}
        />
      ) : null}
      {dialog?.kind === "control" ? (
        <ControlDialog
          api={operations.api}
          taskId={dialog.taskId}
          label={dialog.label}
          pause={dialog.pause}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={finish("background_job")}
        />
      ) : null}
      {dialog?.kind === "reconcile" ? (
        <ReconcileDialog
          payments={payments}
          paymentRequestId={dialog.paymentRequestId}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={finish("payment_reconciliation")}
        />
      ) : null}
      {dialog?.kind === "resolve" ? (
        <ResolveExceptionDialog
          api={api}
          exception={dialog.exception}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={finish("payment_reconciliation")}
        />
      ) : null}
    </section>
  );
}
